// src/pages/PersonalDetailsPage.tsx — Slice D commit 4.
//
// Route: /me/details. Ported from docs/prototype/apps2.jsx lines 495-543.
// Three Cards — SSO-only read-only notice (top, conditional), Contact +
// Address (single Card split with a Hairline), Emergency contact (signal-
// bordered Card with the ✚ icon header).
//
// Save sends the full field set to PUT /api/personal. Empty strings
// normalise to null in the API per allowlisted-upsert semantics — so a
// user clearing a field by deleting the text actually clears the column.
//
// Phase 9 follow-up (2026-05-21) — PIN volunteers don't see the Line
// manager Card and `line_manager_name` is omitted from the PUT body for
// them. Pairs with the same gates on MePage + OnboardingWizard.
//
// Phase 9 follow-up (2026-05-21, second pass) — First aider declaration
// Card. Lets the user self-declare (or revoke) first-aider status via
// PATCH /api/me/first-aider. Inline switch + declaration text confirming
// they hold a qualification AND are willing to be called to an incident.
// Saves immediately on toggle (optimistic flip, revert on error).

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import {
  Button,
  Card,
  Eyebrow,
  Field,
  Hairline,
  Pill,
  TopBar,
} from "../components/ui";

interface ContactRow {
  id: number;
  first: string;
  last: string;
  isManager: boolean;
}

interface ContactsListResponse {
  contacts: ContactRow[];
}

// ---------------------------------------------------------------------------
// Types — mirror the API response shape from /api/personal
// ---------------------------------------------------------------------------

interface PersonalResponse {
  personal_email: string | null;
  mobile_number: string | null;
  home_phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  postcode: string | null;
  emergency_name: string | null;
  emergency_relationship: string | null;
  emergency_phone: string | null;
  emergency_alt_phone: string | null;
  updated_at: string | null;
}

interface ContactForm {
  email: string;
  mobile: string;
  homePhone: string;
  addr1: string;
  addr2: string;
  postcode: string;
}

interface EmergencyForm {
  name: string;
  rel: string;
  phone: string;
  alt: string;
}

const BLANK_CONTACT: ContactForm = {
  email: "",
  mobile: "",
  homePhone: "",
  addr1: "",
  addr2: "",
  postcode: "",
};
const BLANK_EMERGENCY: EmergencyForm = { name: "", rel: "", phone: "", alt: "" };

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---------------------------------------------------------------------------
// Inline switch — iOS-style pill toggle. One-off use for the first-aider
// declaration. Keeps PersonalDetailsPage self-contained.
// ---------------------------------------------------------------------------

function FirstAiderSwitch({
  checked,
  onClick,
  disabled,
}: {
  checked: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onClick}
      disabled={disabled}
      aria-label={checked ? "Revoke first aider declaration" : "Declare first aider status"}
      style={{
        width: 44,
        height: 26,
        borderRadius: 13,
        background: checked ? C.brassDk : C.paperWarm,
        border: `1px solid ${checked ? C.brassDk : C.hairline}`,
        position: "relative",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "background 0.2s, border-color 0.2s",
        padding: 0,
        flexShrink: 0,
        marginTop: 2,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 20 : 2,
          width: 20,
          height: 20,
          borderRadius: 10,
          background: checked ? C.brassGlow : C.card,
          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
          transition: "left 0.2s",
        }}
        aria-hidden="true"
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PersonalDetailsPage() {
  const navigate = useNavigate();
  const { me, refresh } = useCurrentUser();

  // Phase 9 follow-up (2026-05-21) — PIN volunteers don't have a line
  // manager surface anywhere in the app. Drop the Card + skip the field
  // from the PUT body so existing hub_users.line_manager_name is preserved.
  const showLineManager = me?.authMethod !== "pin";

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [contact, setContact] = useState<ContactForm>(BLANK_CONTACT);
  const [emergency, setEmergency] = useState<EmergencyForm>(BLANK_EMERGENCY);

  // Slice F line-manager surface — picker id, free-text fallback name, contacts list.
  const [lineManagerContactId, setLineManagerContactId] = useState<number | null>(
    me?.line_manager_contact_id ?? null,
  );
  const [lineManagerName, setLineManagerName] = useState<string>(me?.line_manager_name ?? "");
  const [contacts, setContacts] = useState<ContactRow[] | null>(null);
  // Slice G — manager suggestions + "Manager not listed?" toggle.
  const [managerSuggestions, setManagerSuggestions] = useState<string[]>([]);
  const [freeTextOpen, setFreeTextOpen] = useState<boolean>(
    (me?.line_manager_name ?? "").trim() !== "",
  );

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Slice G — NYMR membership card.
  const [memberMode, setMemberMode] = useState<"display" | "edit">("display");
  const [memberIdInput, setMemberIdInput] = useState<string>("");
  const [memberSaving, setMemberSaving] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);

  // Phase 9 follow-up (2026-05-21) — first-aider self-declaration state.
  // Optimistic flip on toggle; revert if PATCH fails. Synced from
  // /api/me on every refresh so external changes (admin edit) propagate.
  const [isFirstAider, setIsFirstAider] = useState<boolean>(me?.isFirstAider ?? false);
  const [firstAiderSaving, setFirstAiderSaving] = useState(false);
  const [firstAiderError, setFirstAiderError] = useState<string | null>(null);

  useEffect(() => {
    setMemberIdInput(me?.member_id_number !== null && me?.member_id_number !== undefined
      ? String(me.member_id_number)
      : "");
  }, [me?.member_id_number]);

  useEffect(() => {
    setLineManagerContactId(me?.line_manager_contact_id ?? null);
    setLineManagerName(me?.line_manager_name ?? "");
  }, [me?.line_manager_contact_id, me?.line_manager_name]);

  useEffect(() => {
    setIsFirstAider(me?.isFirstAider ?? false);
  }, [me?.isFirstAider]);

  useEffect(() => {
    // Phase 9 follow-up — skip the contacts + suggestions fetches for PIN
    // users since the Card isn't rendered. Saves two round-trips per visit.
    if (!showLineManager) {
      setContacts([]);
      setManagerSuggestions([]);
      return;
    }
    let cancelled = false;
    apiFetch<ContactsListResponse>("/api/contacts")
      .then((res) => {
        if (!cancelled) setContacts(res.contacts);
      })
      .catch(() => {
        if (!cancelled) setContacts([]);
      });
    apiFetch<{ suggestions: string[] }>("/api/me/manager-suggestions")
      .then((res) => {
        if (cancelled) return;
        const seen = new Set<string>();
        const unique = res.suggestions.filter((s) => {
          const k = s.trim().toLowerCase();
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setManagerSuggestions(unique);
      })
      .catch(() => {
        if (!cancelled) setManagerSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showLineManager]);

  const managerOptions = useMemo(() => {
    if (!contacts) return [];
    return contacts.filter((c) => c.isManager).slice(0, 12);
  }, [contacts]);

  useEffect(() => {
    let cancelled = false;
    apiFetch<PersonalResponse>("/api/personal")
      .then((data) => {
        if (cancelled) return;
        setContact({
          email: data.personal_email ?? "",
          mobile: data.mobile_number ?? "",
          homePhone: data.home_phone ?? "",
          addr1: data.address_line1 ?? "",
          addr2: data.address_line2 ?? "",
          postcode: data.postcode ?? "",
        });
        setEmergency({
          name: data.emergency_name ?? "",
          rel: data.emergency_relationship ?? "",
          phone: data.emergency_phone ?? "",
          alt: data.emergency_alt_phone ?? "",
        });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setLoadError(e instanceof ApiError ? e.message : "Could not load details");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-revert "Saved" → "Save changes" after 1.8s, mirroring the prototype.
  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 1800);
    return () => window.clearTimeout(t);
  }, [saved]);

  async function saveMemberId() {
    setMemberError(null);
    const trimmed = memberIdInput.trim();
    const memberIdNumber: number | null = trimmed === "" ? null : Number(trimmed);
    if (memberIdNumber !== null && !/^\d{3,7}$/.test(trimmed)) {
      setMemberError("Membership number must be 3–7 digits, or leave blank to clear.");
      return;
    }
    setMemberSaving(true);
    try {
      await apiFetch("/api/me/member-id", {
        method: "PATCH",
        body: JSON.stringify({ memberIdNumber }),
      });
      await refresh();
      setMemberMode("display");
    } catch (e) {
      setMemberError(e instanceof ApiError ? e.message : "Save failed.");
    } finally {
      setMemberSaving(false);
    }
  }

  // Phase 9 follow-up — flip is_first_aider, audited server-side. Optimistic
  // flip for snappy UX; revert on error so the UI reflects the actual state.
  async function toggleFirstAider() {
    if (firstAiderSaving) return;
    const next = !isFirstAider;
    setFirstAiderError(null);
    setFirstAiderSaving(true);
    setIsFirstAider(next);
    try {
      await apiFetch("/api/me/first-aider", {
        method: "PATCH",
        body: JSON.stringify({ isFirstAider: next }),
      });
      await refresh();
    } catch (e) {
      setIsFirstAider(!next);
      setFirstAiderError(e instanceof ApiError ? e.message : "Couldn't update first-aider status.");
    } finally {
      setFirstAiderSaving(false);
    }
  }

  async function save() {
    setSaveError(null);
    if (contact.email && !EMAIL_REGEX.test(contact.email.trim())) {
      setSaveError("Personal email is not a valid address");
      return;
    }

    setSaving(true);
    try {
      // Persist personal details + free-text line-manager name in one PUT.
      // The picker (line_manager_contact_id) goes via a separate PATCH so
      // we don't bundle two distinct concerns into the personal allowlist.
      //
      // Phase 9 follow-up (2026-05-21) — omit line_manager_name from the
      // body for PIN sessions so existing hub_users.line_manager_name is
      // preserved (the Card is hidden so the user couldn't edit it anyway).
      const body: Record<string, unknown> = {
        personal_email: contact.email,
        mobile_number: contact.mobile,
        home_phone: contact.homePhone,
        address_line1: contact.addr1,
        address_line2: contact.addr2,
        postcode: contact.postcode,
        emergency_name: emergency.name,
        emergency_relationship: emergency.rel,
        emergency_phone: emergency.phone,
        emergency_alt_phone: emergency.alt,
      };
      if (showLineManager) {
        body.line_manager_name = lineManagerName;
      }
      await apiFetch("/api/personal", {
        method: "PUT",
        body: JSON.stringify(body),
      });

      // Apply picker change only when it differs from the session shape —
      // avoids audit noise on every save. Belt + braces: gate on
      // showLineManager too so PIN sessions never write the picker
      // (state stays null for them since the picker is hidden).
      if (
        showLineManager &&
        lineManagerContactId !== (me?.line_manager_contact_id ?? null)
      ) {
        await apiFetch("/api/me/line-manager", {
          method: "PATCH",
          body: JSON.stringify({ contactId: lineManagerContactId }),
        });
      }

      await refresh();
      setSaved(true);
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <TopBar
        title="My details"
        sub="Personal & emergency information"
        onBack={() => navigate("/me")}
      />
      <div
        style={{
          padding: "14px 16px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {loadError && (
          <Card>
            <div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>
              {loadError}
            </div>
          </Card>
        )}

        {/* SSO read-only notice — only when signed in via SSO */}
        {me?.authMethod === "sso" && (
          <Card>
            <Eyebrow>From Microsoft account</Eyebrow>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 12,
                background: C.heatherBg,
                marginTop: 8,
                fontFamily: F.serif,
                fontStyle: "italic",
                fontSize: 13,
                color: C.heatherDk,
                lineHeight: 1.5,
              }}
            >
              Name and job title come from your Microsoft account. Contact IT to update.
            </div>
          </Card>
        )}

        {loading ? (
          <div
            style={{
              height: 360,
              borderRadius: 18,
              background: C.paperWarm,
              border: `1px solid ${C.hairlineLt}`,
            }}
            aria-hidden="true"
          />
        ) : (
          <>
            {/* Contact + Address */}
            <Card>
              <Eyebrow>Contact</Eyebrow>
              <div style={{ marginTop: 8 }}>
                <Field
                  label="Personal email"
                  value={contact.email}
                  onChange={(v) => setContact({ ...contact, email: v })}
                  type="email"
                  ph="you@example.com"
                />
                <Field
                  label="Mobile"
                  value={contact.mobile}
                  onChange={(v) => setContact({ ...contact, mobile: v })}
                  type="tel"
                  ph="07700 900000"
                />
                <Field
                  label="Home phone"
                  value={contact.homePhone}
                  onChange={(v) => setContact({ ...contact, homePhone: v })}
                  type="tel"
                />
              </div>
              <Hairline style={{ margin: "4px 0 14px" }} />
              <Eyebrow style={{ marginBottom: 6 }}>Home address</Eyebrow>
              <Field
                label="Address line 1"
                value={contact.addr1}
                onChange={(v) => setContact({ ...contact, addr1: v })}
              />
              <Field
                label="Town"
                value={contact.addr2}
                onChange={(v) => setContact({ ...contact, addr2: v })}
              />
              <Field
                label="Postcode"
                value={contact.postcode}
                onChange={(v) => setContact({ ...contact, postcode: v })}
              />
            </Card>

            {/* Line manager — Slice F. SSO-only per Phase 9 follow-up. */}
            {showLineManager && (
              <Card>
                <Eyebrow>Line manager</Eyebrow>
                {contacts === null ? (
                  <div
                    style={{
                      fontFamily: F.serif,
                      fontStyle: "italic",
                      color: C.inkMute,
                      padding: "8px 0",
                      marginTop: 6,
                    }}
                  >
                    Loading managers...
                  </div>
                ) : managerOptions.length === 0 ? (
                  <div
                    style={{
                      fontFamily: F.serif,
                      fontStyle: "italic",
                      color: C.inkMute,
                      padding: "8px 0",
                      marginTop: 6,
                    }}
                  >
                    No managers in the contacts directory yet.
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginTop: 8,
                    }}
                  >
                    {managerOptions.map((m) => {
                      const active = lineManagerContactId === m.id;
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setLineManagerContactId(active ? null : m.id)}
                          style={{
                            padding: "9px 12px",
                            borderRadius: 10,
                            border: active
                              ? `1.5px solid ${C.brassDk}`
                              : `1px solid ${C.hairline}`,
                            background: active ? C.brassBg : C.card,
                            color: active ? C.brassDk : C.ink,
                            fontFamily: F.body,
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {m.first} {m.last}
                        </button>
                      );
                    })}
                  </div>
                )}
                {/* Suggestion pills + "Manager not listed?" toggle — Slice G */}
                {managerSuggestions.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <Eyebrow style={{ marginBottom: 6 }}>Or pick from suggestions</Eyebrow>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {managerSuggestions.map((s) => {
                        const active =
                          lineManagerName.trim().toLowerCase() === s.trim().toLowerCase();
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() => {
                              setLineManagerName(s);
                              setLineManagerContactId(null);
                              setFreeTextOpen(true);
                            }}
                            style={{
                              padding: "7px 11px",
                              borderRadius: 999,
                              border: active
                                ? `1.5px solid ${C.brassDk}`
                                : `1px solid ${C.hairline}`,
                              background: active ? C.brassBg : C.paperWarm,
                              color: active ? C.brassDk : C.inkSoft,
                              fontFamily: F.body,
                              fontSize: 12.5,
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 14 }}>
                  {!freeTextOpen ? (
                    <button
                      type="button"
                      onClick={() => setFreeTextOpen(true)}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                      }}
                    >
                      <Pill tone="heather" small>
                        Manager not listed?
                      </Pill>
                    </button>
                  ) : (
                    <>
                      <Field
                        label="Manager name"
                        value={lineManagerName}
                        onChange={setLineManagerName}
                        ph="Type your line manager's name"
                        max={200}
                      />
                      <div
                        style={{
                          fontFamily: F.serif,
                          fontStyle: "italic",
                          fontSize: 12,
                          color: C.inkMute,
                          lineHeight: 1.45,
                          margin: "-8px 2px 0",
                        }}
                      >
                        Use this when your line manager isn&apos;t in the contacts directory.
                      </div>
                    </>
                  )}
                </div>
              </Card>
            )}

            {/* ID Card Number (Phase 11 / Q1 — relabelled from "NYMR membership"
                / "Member number"; the underlying `member_id_number` field
                is unchanged). */}
            <Card>
              <Eyebrow>ID Card Number</Eyebrow>
              {memberMode === "display" ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    marginTop: 10,
                  }}
                >
                  {me?.member_id_number !== null && me?.member_id_number !== undefined ? (
                    <div
                      style={{
                        fontFamily: F.mono,
                        fontSize: 17,
                        fontWeight: 800,
                        color: C.brassDk,
                        letterSpacing: 0.3,
                      }}
                    >
                      ID Card #{me.member_id_number}
                    </div>
                  ) : (
                    <div
                      style={{
                        fontFamily: F.serif,
                        fontStyle: "italic",
                        fontSize: 14,
                        color: C.inkMute,
                        fontWeight: 600,
                      }}
                    >
                      Not linked
                    </div>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setMemberError(null);
                      setMemberMode("edit");
                    }}
                  >
                    {me?.member_id_number !== null && me?.member_id_number !== undefined
                      ? "Edit"
                      : "Link your ID Card"}
                  </Button>
                </div>
              ) : (
                <div style={{ marginTop: 10 }}>
                  <Field
                    label="ID Card Number"
                    value={memberIdInput}
                    onChange={setMemberIdInput}
                    type="number"
                    ph="e.g. 27980"
                  />
                  <div
                    style={{
                      fontFamily: F.serif,
                      fontStyle: "italic",
                      fontSize: 12,
                      color: memberError ? C.signalDk : C.inkMute,
                      lineHeight: 1.45,
                      margin: "-6px 2px 12px",
                      fontWeight: 600,
                    }}
                  >
                    {memberError ?? (
                      <>
                        Typically 4–6 digits, e.g. 27980. Leave blank to clear your link.
                      </>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Button
                      variant="primary"
                      fullWidth
                      disabled={memberSaving}
                      onClick={saveMemberId}
                    >
                      {memberSaving ? "Saving..." : "Save"}
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={memberSaving}
                      onClick={() => {
                        setMemberError(null);
                        setMemberIdInput(
                          me?.member_id_number !== null && me?.member_id_number !== undefined
                            ? String(me.member_id_number)
                            : "",
                        );
                        setMemberMode("display");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* First aider self-declaration — Phase 9 follow-up (2026-05-21).
                Inline switch + duty-of-care confirmation text. Saves
                immediately via PATCH /api/me/first-aider (audited). */}
            <Card>
              <Eyebrow>First aider</Eyebrow>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 14,
                  marginTop: 10,
                }}
              >
                <FirstAiderSwitch
                  checked={isFirstAider}
                  onClick={toggleFirstAider}
                  disabled={firstAiderSaving}
                />
                <p
                  style={{
                    fontFamily: F.serif,
                    fontStyle: "italic",
                    fontSize: 13,
                    color: C.inkSoft,
                    fontWeight: 600,
                    lineHeight: 1.5,
                    margin: 0,
                    flex: 1,
                  }}
                >
                  By declaring you are a first aider, you are confirming you have a First Aider
                  qualification or Training and you are willing to be called to a First Aid
                  incident whilst on duty on the railway.
                </p>
              </div>
              {firstAiderError && (
                <div
                  style={{
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: C.signalBg,
                    border: "1px solid rgba(184,58,43,0.22)",
                    color: C.signalDk,
                    fontFamily: F.body,
                    fontWeight: 700,
                    fontSize: 13,
                    marginTop: 12,
                  }}
                  role="status"
                >
                  {firstAiderError}
                </div>
              )}
            </Card>

            {/* Emergency contact */}
            <div
              style={{
                background: C.card,
                borderRadius: 18,
                border: `1px solid rgba(184,58,43,0.22)`,
                boxShadow: C.shadowS,
                padding: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: C.signalBg,
                    border: `1px solid rgba(184,58,43,0.22)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  aria-hidden="true"
                >
                  <span
                    style={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: C.signalDk,
                      lineHeight: 1,
                    }}
                  >
                    ✚
                  </span>
                </div>
                <div>
                  <Eyebrow c={C.signalDk}>Emergency contact</Eyebrow>
                  <div
                    style={{
                      fontFamily: F.serif,
                      fontSize: 18,
                      fontWeight: 700,
                      color: C.ink,
                      letterSpacing: -0.2,
                    }}
                  >
                    In case of emergency
                  </div>
                </div>
              </div>
              <Field
                label="Contact name"
                value={emergency.name}
                onChange={(v) => setEmergency({ ...emergency, name: v })}
              />
              <Field
                label="Relationship"
                value={emergency.rel}
                onChange={(v) => setEmergency({ ...emergency, rel: v })}
              />
              <Field
                label="Primary phone"
                value={emergency.phone}
                onChange={(v) => setEmergency({ ...emergency, phone: v })}
                type="tel"
              />
              <Field
                label="Alternative phone"
                value={emergency.alt}
                onChange={(v) => setEmergency({ ...emergency, alt: v })}
                type="tel"
              />
            </div>

            {saveError && (
              <Card>
                <div
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    background: C.signalBg,
                    border: `1px solid rgba(184,58,43,0.22)`,
                    color: C.signalDk,
                    fontFamily: F.body,
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {saveError}
                </div>
              </Card>
            )}

            <Button
              variant={saved ? "sage" : "primary"}
              fullWidth
              disabled={saving}
              onClick={save}
            >
              {saving ? "Saving..." : saved ? "✓ Details saved" : "Save changes"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
